// Enhanced simulations page with what-if scenarios
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
// supabase import removed - using SimulationEngine
import { useProject } from '@/contexts/project-context'
import { SimulationEngine } from '@/lib/simulation-engine'
import { SimulationResults } from '@/types/simulation'
import SimulationWizard from '@/components/simulation-wizard'
import SimulationComparison from '@/components/simulation-comparison'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Plus,
  Play,
  Clock,
  UserPlus,
  DollarSign,
  TrendingUp,
  Target,
  Zap,
  BarChart3,
  Eye,
  Save,
  GitCompare,
  TrendingDown,
  AlertCircle
} from 'lucide-react'
import { format } from 'date-fns'
import { toast } from 'sonner'

export default function SimulationsPage() {
  const [showWizard, setShowWizard] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [showComparisonDialog, setShowComparisonDialog] = useState(false)
  const [currentResults, setCurrentResults] = useState<SimulationResults | null>(null)
  const [selectedSimulations, setSelectedSimulations] = useState<string[]>([])
  const [saveName, setSaveName] = useState('')
  const [saveDescription, setSaveDescription] = useState('')
  
  const { currentProject } = useProject()
  const queryClient = useQueryClient()

  // Fetch saved simulations
  const { data: simulations, isLoading } = useQuery({
    queryKey: ['simulations', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      return SimulationEngine.listSimulations(currentProject.id)
    },
    enabled: !!currentProject
  })

  // Save simulation mutation
  const { mutate: saveSimulation, isPending: isSaving } = useMutation({
    mutationFn: async ({ name, description }: { name: string; description: string }) => {
      if (!currentProject || !currentResults) throw new Error('No simulation to save')
      
      const engine = new SimulationEngine(currentProject.id)
      return engine.saveSimulation(
        name,
        description,
        'new_hire', // Would be dynamic based on actual scenario
        { effective_date: new Date().toISOString() }, // Include required parameters
        currentResults
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simulations'] })
      setShowSaveDialog(false)
      toast.success('Simulation saved successfully')
    },
    onError: () => {
      toast.error('Failed to save simulation')
    }
  })

  const handleSimulationComplete = (results: SimulationResults) => {
    setCurrentResults(results)
    setShowResults(true)
  }

  const handleCompareSimulations = () => {
    if (selectedSimulations.length < 2) {
      toast.error('Select at least 2 simulations to compare')
      return
    }
    setShowComparisonDialog(true)
  }

  const renderResultsDialog = () => {
    if (!currentResults) return null

    const { cap_table_before, cap_table_after, dilution_impact, financial_impact, exit_payouts, warnings } = currentResults

    return (
      <Dialog open={showResults} onOpenChange={setShowResults}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Simulation Results</DialogTitle>
            <DialogDescription>
              Analysis of your what-if scenario
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Warnings */}
            {warnings && warnings.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                  <div>
                    <h4 className="font-medium text-yellow-900">Warnings</h4>
                    <ul className="mt-1 text-sm text-yellow-700 list-disc list-inside">
                      {warnings.map((warning, i) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Financial Impact */}
            {financial_impact && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Financial Impact</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {financial_impact.cash_inflow && (
                      <div>
                        <p className="text-sm text-gray-500">Cash Inflow</p>
                        <p className="text-lg font-semibold text-green-600">
                          ${financial_impact.cash_inflow.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {financial_impact.cash_outflow && (
                      <div>
                        <p className="text-sm text-gray-500">Cash Outflow</p>
                        <p className="text-lg font-semibold text-red-600">
                          ${financial_impact.cash_outflow.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {financial_impact.new_valuation && (
                      <div>
                        <p className="text-sm text-gray-500">New Valuation</p>
                        <p className="text-lg font-semibold">
                          ${financial_impact.new_valuation.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {financial_impact.price_per_share && (
                      <div>
                        <p className="text-sm text-gray-500">Price/Share</p>
                        <p className="text-lg font-semibold">
                          ${financial_impact.price_per_share.toFixed(4)}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dilution Impact */}
            {dilution_impact && dilution_impact.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Dilution Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dilution_impact.map((impact) => (
                      <div key={impact.user_id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <p className="font-medium">{impact.name}</p>
                          <p className="text-sm text-gray-500">
                            {impact.shares_before.toLocaleString()} → {impact.shares_after.toLocaleString()} shares
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">
                            {impact.percentage_before.toFixed(2)}% → {impact.percentage_after.toFixed(2)}%
                          </p>
                          <p className={`text-sm ${impact.dilution_percentage > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {impact.dilution_percentage > 0 ? <TrendingDown className="inline h-3 w-3" /> : null}
                            {impact.dilution_percentage.toFixed(2)}% dilution
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Exit Payouts */}
            {exit_payouts && exit_payouts.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Exit Payouts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {exit_payouts.map((payout) => (
                      <div key={payout.user_id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <p className="font-medium">{payout.name}</p>
                          <p className="text-sm text-gray-500">
                            {payout.shares.toLocaleString()} {payout.equity_class} shares
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-green-600">
                            ${payout.net_payout.toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-500">
                            {payout.percentage_of_proceeds.toFixed(2)}% of proceeds
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cap Table Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Cap Table Changes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-2">Before</h4>
                    <dl className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Total Shares</dt>
                        <dd>{cap_table_before.total_shares.toLocaleString()}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Share Price</dt>
                        <dd>${cap_table_before.share_price.toFixed(4)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Valuation</dt>
                        <dd>${cap_table_before.valuation.toLocaleString()}</dd>
                      </div>
                    </dl>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">After</h4>
                    <dl className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Total Shares</dt>
                        <dd>{cap_table_after.total_shares.toLocaleString()}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Share Price</dt>
                        <dd>${cap_table_after.share_price.toFixed(4)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Valuation</dt>
                        <dd>${cap_table_after.valuation.toLocaleString()}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResults(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowResults(false)
              setShowSaveDialog(true)
            }}>
              <Save className="h-4 w-4 mr-2" />
              Save Simulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  const getScenarioIcon = (type: string) => {
    switch (type) {
      case 'new_hire': return UserPlus
      case 'funding_round': return DollarSign
      case 'exit': return TrendingUp
      case 'milestone': return Target
      case 'option_exercise': return Zap
      default: return Play
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">What-If Simulations</h1>
          <p className="mt-1 text-sm text-gray-600">
            Model equity scenarios to understand their impact
          </p>
        </div>
        <div className="flex gap-2">
          {selectedSimulations.length > 0 && (
            <Button
              variant="outline"
              onClick={handleCompareSimulations}
              disabled={selectedSimulations.length < 2}
            >
              <GitCompare className="h-4 w-4 mr-2" />
              Compare ({selectedSimulations.length})
            </Button>
          )}
          <Button onClick={() => setShowWizard(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Simulation
          </Button>
        </div>
      </div>

      {/* Scenario Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowWizard(true)}>
          <CardHeader>
            <CardTitle className="flex items-center text-base">
              <UserPlus className="h-5 w-5 mr-2 text-blue-600" />
              New Hire Impact
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Model how adding team members affects equity distribution and dilution
            </p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowWizard(true)}>
          <CardHeader>
            <CardTitle className="flex items-center text-base">
              <DollarSign className="h-5 w-5 mr-2 text-green-600" />
              Funding Scenarios
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Simulate investment rounds and see dilution across all shareholders
            </p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowWizard(true)}>
          <CardHeader>
            <CardTitle className="flex items-center text-base">
              <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
              Exit Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Calculate payouts for acquisition or IPO scenarios with waterfall analysis
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Saved Simulations */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-white shadow rounded-lg p-6 animate-pulse">
              <div className="h-24 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : simulations && simulations.length > 0 ? (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-gray-900">Saved Simulations</h2>
          <div className="grid gap-4">
            {simulations.map((sim) => {
              const Icon = getScenarioIcon(sim.scenario_type)
              return (
                <Card key={sim.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <Icon className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{sim.name}</CardTitle>
                          <CardDescription>{sim.description}</CardDescription>
                          <div className="flex items-center mt-2 text-sm text-gray-500">
                            <Clock className="h-3 w-3 mr-1" />
                            {format(new Date(sim.created_at), 'MMM d, yyyy h:mm a')}
                            <Badge variant="outline" className="ml-3">
                              {sim.scenario_type.replace('_', ' ')}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (selectedSimulations.includes(sim.id)) {
                              setSelectedSimulations(selectedSimulations.filter(id => id !== sim.id))
                            } else {
                              setSelectedSimulations([...selectedSimulations, sim.id])
                            }
                          }}
                        >
                          {selectedSimulations.includes(sim.id) ? '✓' : 'Select'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (sim.results) {
                              setCurrentResults(sim.results)
                              setShowResults(true)
                            }
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              )
            })}
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No saved simulations yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Run your first simulation to start exploring scenarios
            </p>
          </CardContent>
        </Card>
      )}

      {/* Simulation Wizard */}
      <SimulationWizard
        open={showWizard}
        onOpenChange={setShowWizard}
        onComplete={handleSimulationComplete}
      />

      {/* Results Dialog */}
      {renderResultsDialog()}

      {/* Save Simulation Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Simulation</DialogTitle>
            <DialogDescription>
              Save this simulation to compare with others later
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Name</Label>
              <Input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g., Series A at $10M valuation"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                placeholder="Describe the key assumptions and parameters..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveSimulation({ name: saveName, description: saveDescription })}
              disabled={!saveName || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comparison Dialog */}
      {simulations && (
        <SimulationComparison
          simulations={simulations.filter(s => selectedSimulations.includes(s.id))}
          open={showComparisonDialog}
          onOpenChange={setShowComparisonDialog}
        />
      )}
    </div>
  )
}